<template>
  <a-breadcrumb>
    <a-breadcrumb-item>分销管理</a-breadcrumb-item>
    <a-breadcrumb-item>抖音原生分销片库</a-breadcrumb-item>
  </a-breadcrumb>
  <div class="tool-view">
    <a-row class="tool-row tool-header-row" :gutter="[16, 16]">
      <a-col :span="6">
        <a-row>
          <a-col :span="6" class="tool-title">剧名：</a-col>
          <a-col :span="18">
            <a-input v-model:value="nameModel" class="tool-item" placeholder="请输入" allow-clear />
          </a-col>
        </a-row>
      </a-col>
      <a-col :span="6">
        <a-row>
          <a-col :span="6" class="tool-title">类型：</a-col>
          <a-col :span="18">
            <a-select
              v-model:value="genre"
              class="tool-item"
              placeholder="请选择"
              allow-clear
              :getPopupContainer="(triggerNode) => triggerNode.parentNode"
            >
              <a-select-option v-for="item in DysPlayletGenres" :key="item.value" :value="item.value">
                {{ item.text }}
              </a-select-option>
            </a-select>
          </a-col>
        </a-row>
      </a-col>
      <a-col :span="6">
        <a-space>
          <a-button type="primary" @click="touchSearch">查询</a-button>
          <a-button @click="touchReset">重置</a-button>
        </a-space>
      </a-col>
      <a-col :span="6" style="text-align: right">
        <a-button type="primary" :loading="isSyncing" :disabled="isSyncing" @click="touchCrawl">
          抓取片库
        </a-button>
      </a-col>
    </a-row>
  </div>
  <a-table
    class="table-view"
    :data-source="dataSource"
    :columns="columns"
    :row-key="record => record.id"
    :pagination="pagination"
    :loading="isLoading"
    @change="handleTableChange"
  >
    <template #bodyCell="{ column, record }">
      <template v-if="column.key === 'remark_name'">
        {{ record.remark_name || '-' }}
      </template>
      <template v-else-if="column.key === 'dist_switch'">
        <a-switch
          checked-children="开"
          un-checked-children="关"
          :disabled="isLoading"
          :checked="isResellerOn(record)"
          @change="checked => changeDistSwitch(record, checked)"
        />
      </template>
      <template v-else-if="column.key === 'album_id'">
        {{ record.douyin_drama_id || '-' }}
      </template>
      <template v-else-if="column.key === 'episodes'">
        {{ record.total_ep != null ? `${record.total_ep}集` : '-' }}
      </template>
      <template v-else-if="column.key === 'type_label'">
        {{ genreLabel(record) }}
      </template>
      <template v-else-if="column.key === 'dy_release_date'">
        {{ formatDate(record.publish_time) }}
      </template>
      <template v-else-if="column.key === 'operation'">
        <a class="operation-link" @click.prevent="refPaidLinkDrawer?.showDrawer(record)">付费链接</a>
        <a class="operation-link" @click.prevent="refFreeLinkDrawer?.showDrawer(record)">免费链接</a>
      </template>
    </template>
  </a-table>
  <PaidLinkDrawer ref="refPaidLinkDrawer" />
  <FreeLinkDrawer ref="refFreeLinkDrawer" />
  <AddPaidPanelModal ref="refAddPaidPanelModal" />
</template>

<script setup>
import { onBeforeMount, reactive, ref } from 'vue'
import { message } from 'ant-design-vue'
import dayjs from 'dayjs'
import { dysPlayletList, dysPlayletSync, dysPlayletStatus } from '@/api/copyright'
import { DysPlayletGenres } from '@/utils/constantList'
import PaidLinkDrawer from './components-dy-native-film-library/PaidLinkDrawer.vue'
import FreeLinkDrawer from './components-dy-native-film-library/FreeLinkDrawer.vue'
import AddPaidPanelModal from './components-dy-native-film-library/AddPaidPanelModal.vue'

const isLoading = ref(false)
const isSyncing = ref(false)
const refPaidLinkDrawer = ref(null)
const refFreeLinkDrawer = ref(null)
const refAddPaidPanelModal = ref(null)
const nameModel = ref(undefined)
const name = ref(undefined)
const genre = ref(undefined)
const dataSource = ref([])

const pagination = reactive({
  total: 0,
  current: 1,
  pageSize: 10,
  showSizeChanger: true,
  pageSizeOptions: ['10', '20', '50', '100'],
  showTotal: total => `总共 ${total} 条`
})

const columns = ref([
  { title: '剧名', dataIndex: 'remark_name', key: 'remark_name', width: 200 },
  { title: '分销开关', key: 'dist_switch', width: 100, align: 'center' },
  { title: '抖音专辑ID', dataIndex: 'album_id', key: 'album_id', width: 180 },
  { title: '集数', key: 'episodes', width: 88 },
  { title: '类型', key: 'type_label', width: 100 },
  { title: '抖音发布日期', key: 'dy_release_date', width: 130 },
  { title: '操作', key: 'operation', width: 160, fixed: 'right' }
])

onBeforeMount(() => {
  touchSearch()
})

function formatDate(val) {
  if (val == null || val === '') return '-'
  const d = dayjs(val)
  return d.isValid() ? d.format('YYYY-MM-DD') : String(val)
}

function touchSearch() {
  name.value = nameModel.value
  pagination.current = 1
  getList()
}

function touchReset() {
  nameModel.value = undefined
  name.value = undefined
  genre.value = undefined
  touchSearch()
}

function touchCrawl() {
  isSyncing.value = true
  dysPlayletSync()
    .then(res => {
      const { code, msg, message: m } = res || {}
      if (code === 0) {
        message.success(m || msg || '抓取成功')
        getList()
      } else {
        message.error(m || msg || '抓取失败')
      }
    })
    .catch(() => {
      message.error('抓取失败')
    })
    .finally(() => {
      isSyncing.value = false
    })
}

function isResellerOn(record) {
  return Number(record.reseller_status) === 1
}

function genreLabel(record) {
  const g = Number(record.genre)
  const hit = DysPlayletGenres.find(item => item.value === g)
  return hit?.text || record.genre_name || '-'
}

function changeDistSwitch(record, checked) {
  dysPlayletStatus({
    id: record.id,
    reseller_status: checked ? 1 : 0
  })
    .then(res => {
      const { code, msg, message: m } = res
      if (code === 0) {
        message.success(m || msg || '操作成功')
      } else {
        message.error(m || msg || '操作失败')
      }
    })
    .catch(() => {
      message.error('操作失败')
    })
    .finally(() => {
      getList()
    })
}

function handleTableChange(p) {
  pagination.current = p.current
  pagination.pageSize = p.pageSize
  getList()
}

function normalizePlayletRow(item) {
  return {
    ...item,
    remark_name: item.book_name || item.root_book_name || '-',
    douyin_drama_id: item.series_id,
    unit_episode_price: item.unit_price
  }
}

function getList() {
  isLoading.value = true
  const params = {
    page: String(pagination.current),
    page_size: String(pagination.pageSize)
  }
  if (name.value != null && String(name.value).trim() !== '') {
    params.name = String(name.value).trim()
  }
  if (genre.value != null && genre.value !== '') {
    params.genre = String(genre.value)
  }
  dysPlayletList(params)
    .then(res => {
      const { code, data } = res
      if (code === 0) {
        const list = data?.data || []
        dataSource.value = list.map(normalizePlayletRow)
        pagination.total = data?.total ?? 0
      }
    })
    .catch(() => {
      message.error('加载失败')
    })
    .finally(() => {
      isLoading.value = false
    })
}
</script>

<style scoped>
.tool-header-row {
  align-items: flex-end;
}
.operation-link {
  margin-right: 12px;
}
.operation-link:last-child {
  margin-right: 0;
}
</style>
